<template>
    <div class="max-w-(--breakpoint-md) flex flex-col gap-4">
        <page-header>Revel</page-header>

        <vx-expandable-card>
            <template v-slot:header>Revel Integration</template>
            <template v-slot:headerRight>
                <div v-if="connectionStatus === 'loading'">
                    <vx-badge color="slate">Loading</vx-badge>
                </div>
                <div v-else-if="connectionStatus === 'connected'">
                    <vx-badge color="success">Connected</vx-badge>
                </div>
                <div v-else>
                    <vx-badge color="slate">Not connected</vx-badge>
                </div>
            </template>

            <template v-slot:content>
                <div v-if="!hasPermission" class="flex flex-col px-8 py-20 gap-6 items-center text-xl text-slate-500">
                    Only the team owner can configure integrations.
                </div>
                <div v-else-if="connectionStatus === 'connected'" class="flex flex-col gap-6 mt-3">
                    <div>
                        To regenerate the webhook URLs, click the button below. You will need to update all URLs in your
                        <a
                            href="https://support.revelsystems.com/s/webhook-access"
                            target="_blank"
                            rel="noopener noreferrer"
                            class="text-blue-600 hover:underline"
                        >
                            Revel system</a
                        >.
                    </div>

                    <div class="flex gap-3">
                        <vx-button
                            color="primary"
                            size="lg"
                            @click="regenerateWebhookUrl"
                            :loading="generating"
                            data-test="regenerate-button"
                        >
                            Regenerate Webhook URLs
                        </vx-button>

                        <vx-button
                            color="danger"
                            size="lg"
                            @click="disconnectRevel"
                            :loading="disconnecting"
                            data-test="disconnect-button"
                        >
                            Disconnect
                        </vx-button>
                    </div>
                </div>
                <div v-else-if="connectionStatus === 'not_connected'" class="flex flex-col gap-6 mt-3">
                    <div class="mb-4">
                        Click the button below to generate webhook URLs for your Revel integration. You will need to
                        copy the URLs and configure them in your
                        <a
                            href="https://support.revelsystems.com/s/webhook-access"
                            target="_blank"
                            rel="noopener noreferrer"
                            class="text-blue-600 hover:underline"
                        >
                            Revel system</a
                        >.
                    </div>

                    <vx-button size="lg" @click="generateWebhookUrl" :loading="generating" data-test="generate-button">
                        Generate Webhook URLs
                    </vx-button>
                </div>
            </template>
        </vx-expandable-card>

        <revel-documentation />

        <integration-logs-card v-if="FEATURES.integration_logs" vendor="revel" />

        <!-- Webhook URL Modal -->
        <vx-modal
            v-model:visible="showWebhookUrlModal"
            preventClosing
            padding="standard"
            size="lg"
            data-test="webhook-url-modal"
        >
            <template v-slot:header>Webhook URLs</template>

            <vx-alert color="info" :closeable="false">
                Your webhook URLs have been generated. Please copy them now and configure them in your
                <a
                    href="https://support.revelsystems.com/s/webhook-access"
                    target="_blank"
                    rel="noopener noreferrer"
                    class="text-blue-600 hover:underline"
                >
                    Revel system</a
                >.<br />
                <strong>These URLs will only be shown once.</strong>
            </vx-alert>

            <div class="space-y-3 mt-4">
                <div>
                    <div class="text-sm font-medium mb-2">Order Finalized</div>
                    <vx-popover hover arrow placement="top">
                        <div
                            @click="copyWebhookUrl(generatedWebhookUrls?.order_finalized, 'Order Finalized')"
                            class="font-mono break-all cursor-pointer p-4 bg-white rounded-sm"
                            data-test="copy-order-finalized-button"
                        >
                            {{ generatedWebhookUrls?.order_finalized }} <font-awesome-icon widthAuto :icon="faCopy" />
                        </div>
                        <template v-slot:content>Click to Copy</template>
                    </vx-popover>
                </div>

                <div>
                    <div class="text-sm font-medium mb-2">Customer Created</div>
                    <vx-popover hover arrow placement="top">
                        <div
                            @click="copyWebhookUrl(generatedWebhookUrls?.customer_created, 'Customer Created')"
                            class="font-mono break-all cursor-pointer p-4 bg-white rounded-sm"
                            data-test="copy-customer-created-button"
                        >
                            {{ generatedWebhookUrls?.customer_created }} <font-awesome-icon widthAuto :icon="faCopy" />
                        </div>
                        <template v-slot:content>Click to Copy</template>
                    </vx-popover>
                </div>

                <div>
                    <div class="text-sm font-medium mb-2">Customer Updated</div>
                    <vx-popover hover arrow placement="top">
                        <div
                            @click="copyWebhookUrl(generatedWebhookUrls?.customer_updated, 'Customer Updated')"
                            class="font-mono break-all cursor-pointer p-4 bg-white rounded-sm"
                            data-test="copy-customer-updated-button"
                        >
                            {{ generatedWebhookUrls?.customer_updated }} <font-awesome-icon widthAuto :icon="faCopy" />
                        </div>
                        <template v-slot:content>Click to Copy</template>
                    </vx-popover>
                </div>
            </div>

            <template v-slot:footer>
                <vx-button block color="primary" size="lg" @click="closeWebhookUrlModal" data-test="close-modal-button">
                    I've copied the URLs
                </vx-button>
            </template>
        </vx-modal>
    </div>
</template>

<script setup>
import { computed, ref, onMounted } from 'vue';
import { FEATURES } from '~/provides/features';
import { VxExpandableCard, VxButton, VxBadge, VxAlert, VxModal, VxPopover, VxSwal } from '@voxie/frontend-components';
import { FontAwesomeIcon } from '@fortawesome/vue-fontawesome';
import { faCopy } from '@fortawesome/pro-solid-svg-icons';
import PageHeader from '~/components/general/PageHeader.vue';
import RevelDocumentation from './revel/RevelDocumentation.vue';
import IntegrationLogsCard from './IntegrationLogsCard.vue';
import teamsClient from '~/services/teamsClient';
import { useToasted } from '~/composables/useToasted';
import { catchRequestError } from '~/utils/request';

const props = defineProps({
    ownsTeam: {
        type: Boolean,
        default: false,
    },
});

const toasted = useToasted();
const connectionStatus = ref('loading');
const generatedWebhookUrls = ref(null);
const showWebhookUrlModal = ref(false);
const generating = ref(false);
const disconnecting = ref(false);

const getConnectionStatus = async () => {
    try {
        const response = await teamsClient.getRevelStatus(Spark.state.currentTeam.id);
        connectionStatus.value = response.data.status;
    } catch (error) {
        console.error(error);
        connectionStatus.value = 'not_connected';
    }
};

onMounted(async () => {
    await getConnectionStatus();
});

const hasPermission = computed(() => props.ownsTeam);

const generateWebhookUrl = async () => {
    generating.value = true;
    try {
        const response = await teamsClient.generateRevelWebhookUrl(Spark.state.currentTeam.id);
        generatedWebhookUrls.value = response.data.webhook_urls;
        connectionStatus.value = 'connected';
        showWebhookUrlModal.value = true;
        toasted.success('Webhook URLs generated successfully.');
    } catch (error) {
        toasted.error(catchRequestError(error));
    } finally {
        generating.value = false;
    }
};

const regenerateWebhookUrl = () => {
    VxSwal.confirmation(
        'Regenerate Webhook URLs',
        'Are you sure you want to regenerate the webhook URLs? You will need to update all URLs in your Revel system, otherwise all webhook events will be ignored.',
        'warning',
        async () => {
            generating.value = true;
            try {
                const response = await teamsClient.generateRevelWebhookUrl(Spark.state.currentTeam.id);
                generatedWebhookUrls.value = response.data.webhook_urls;
                showWebhookUrlModal.value = true;
                toasted.success('Webhook URLs regenerated successfully. Please update all URLs in your Revel system.');
            } catch (error) {
                toasted.error(catchRequestError(error));
            } finally {
                generating.value = false;
            }
        }
    );
};

const copyWebhookUrl = (url, eventType) => {
    navigator.clipboard
        .writeText(url)
        .then(() => {
            toasted.success(`${eventType} webhook URL copied to clipboard.`);
        })
        .catch((error) => {
            console.error(error);
            toasted.error('Failed to copy webhook URL.');
        });
};

const closeWebhookUrlModal = () => {
    showWebhookUrlModal.value = false;
    generatedWebhookUrls.value = null;
};

const disconnectRevel = () => {
    VxSwal.confirmation(
        'Disconnect Revel',
        'Are you sure you want to disconnect your Revel integration? This action cannot be undone.',
        'danger',
        async () => {
            disconnecting.value = true;
            try {
                await teamsClient.disconnectRevel(Spark.state.currentTeam.id);
                connectionStatus.value = 'not_connected';
                toasted.success('Revel integration disconnected successfully.');
            } catch (error) {
                toasted.error(catchRequestError(error));
            } finally {
                disconnecting.value = false;
            }
        }
    );
};
</script>
